import { isDefined } from '@/composables/utils'
import { EndPoint } from '@/enums/EndPoint'
import { SqlComparison } from '@/enums/SqlComparison'
import { SqlOperator } from '@/enums/SqlOperator'
import { ImportRelatedError } from '@/errors/ImportRelatedError'
import type { IImportDefinition } from '@/interfaces/IImportDefinition'
import type { MedicalClearanceRecord } from '@/models/MedicalClearanceRecord'
import { useAPIStore } from '@/stores/db/ApiStore'
import { useSettingsStore } from '@/stores/ui/SettingsStore'
import dayjs from 'dayjs'
import { toNumber } from 'lodash-es'
import { useI18n, type Composer } from 'vue-i18n'
import { displayDate, toBoolean, toISODate, toText } from '.'

async function addCalculatedFields(
  item: MedicalClearanceRecord,
  i18n: Composer<{}, {}, {}, string, never, string>
) {
  const apiStore = useAPIStore()
  const settingsStore = useSettingsStore()
  const personId = Number.parseInt(item.personId as unknown as string) || 0
  if (personId <= 0) {
    const query = {
      isDistinct: true,
      where: [
        {
          field: 'firstName',
          comparison: SqlComparison.Equal,
          value1: item.firstName,
          operator: SqlOperator.And
        },
        {
          field: 'lastName',
          comparison: SqlComparison.Equal,
          value1: item.lastName,
          operator: SqlOperator.And
        },
        {
          field: 'idNumber',
          comparison: SqlComparison.Equal,
          value1: item.idNumber
        }
      ]
    }

    const response = await apiStore.getMany(EndPoint.People, query)
    if (!response.error && response.data.results.length > 0) {
      item.personId = response.data.results[0].id!
    } else {
      throw new ImportRelatedError(
        i18n.t('errors.importTable.messages.importRelatedPerson', {
          lastName: item.lastName,
          firstName: item.firstName,
          idNumber: item.idNumber
        })
      )
    }
  }
  const daysToExpire = settingsStore.globals.clearance.daysToExpire
  if (isDefined(item.clearanceDate) && !isDefined(item.expiration)) {
    item.expiration = dayjs(item.clearanceDate).add(daysToExpire, 'day').toISOString()
  }
}

export const getImportDefinition = (): IImportDefinition => {
  const i18n = useI18n({ useScope: 'global' })
  return {
    allowInsert: false,
    addCalculatedFields,
    endpoint: EndPoint.MedicalClearances,
    keyFields: ['personId'],
    friendlyName: 'tables.names.clearance',
    fields: [
      {
        title: i18n.t('fields.clearance.personId'),
        name: 'personId',
        convert: toNumber,
        calculated: true,
        defaultValue: null
      },
      {
        title: i18n.t('fields.clearance.firstName'),
        name: 'firstName',
        convert: toText(64),
        required: true
      },
      {
        title: i18n.t('fields.clearance.lastName'),
        name: 'lastName',
        convert: toText(64),
        required: true
      },
      {
        title: i18n.t('fields.clearance.idNumber'),
        name: 'idNumber',
        convert: toText(64),
        required: true
      },
      {
        title: i18n.t('fields.clearance.clearanceDate'),
        name: 'clearanceDate',
        aliases: ['clearance'],
        convert: toISODate,
        display: displayDate
      },
      {
        title: i18n.t('fields.clearance.expiration'),
        name: 'expiration',
        aliases: ['expirationdate'],
        convert: toISODate,
        display: displayDate
      },
      {
        title: i18n.t('fields.clearance.restrictions'),
        name: 'restrictions',
        convert: toText(255)
      },
      {
        title: i18n.t('fields.clearance.disposable'),
        name: 'disposable',
        convert: toBoolean,
        aliases: ['singleUse', 'su'],
        defaultValue: false
      },
      {
        title: i18n.t('fields.clearance.hfNPR'),
        name: 'hfNPR',
        convert: toBoolean,
        aliases: ['hfNPR', 'Half Face Negative Pressure Respirator'],
        defaultValue: false
      },
      {
        title: i18n.t('fields.clearance.ffNPR'),
        name: 'ffNPR',
        convert: toBoolean,
        aliases: ['Full Face Negative Pressure Respirator'],
        defaultValue: false
      },
      {
        title: i18n.t('fields.clearance.hfPAPR'),
        name: 'hfPAPR',
        convert: toBoolean,
        aliases: ['Half Facepiece Powered Air Purifying Respirator'],
        defaultValue: false
      },
      {
        title: i18n.t('fields.clearance.ffPAPR'),
        name: 'ffPAPR',
        convert: toBoolean,
        aliases: ['Full Facepiece Powered Air Purifying Respirator'],
        defaultValue: false
      },
      {
        title: i18n.t('fields.clearance.hfSAR'),
        name: 'hfSAR',
        convert: toBoolean,
        aliases: ['Half Face Supplied Air Respirator'],
        defaultValue: false
      },
      {
        title: i18n.t('fields.clearance.ffSAR'),
        name: 'ffSAR',
        convert: toBoolean,
        aliases: ['Full Face Supplied Air Respirator'],
        defaultValue: false
      },
      {
        title: i18n.t('fields.clearance.scba'),
        name: 'scba',
        convert: toBoolean,
        aliases: ['Self Contained Breathing Apparatus'],
        defaultValue: false
      }
    ]
  }
}
